import React, { useCallback, useEffect, useRef, useState } from 'react';
import { fn, logWebEvent } from '../firebase.js';
import { useSession } from '../context/SessionContext.jsx';
import { resolvesLabel } from '../util/etDate.js';

// First-submission BONUS BALLOT — the web founder twist.
//
// On iOS the Day-1 chain ends with a group-invite ask. On web we REPLACE that
// step with an AUTH GATE: a first-time player submits today's six + sees the
// celebration, then must sign up / sign in BEFORE answering a 12-question bonus
// ballot. The ballot's bonus answers earn RESOLUTION points as each question
// settles (same as iOS — no flat coin/point payout up front).
//
// This component owns the whole post-celebration flow as a full-screen overlay:
//
//   gate     → "Sign up to unlock 12 bonus questions" (reuses openSaveAccount).
//              We watch `isAnonymous`; the moment it flips false (the SaveAccount
//              modal linked the account), we advance to `ballot`.
//   ballot   → fetch the server-picked cohort (getWebBonusBallot), render one
//              card at a time, collect picks locally.
//   submitting/done → submit via claimWebBonusBallot, show the reward.
//
// PERSISTENCE / no-double-award:
//   • The SERVER one-shot gate (hasSeenFirstSubmissionBonusWeb) is the real
//     guard — claimWebBonusBallot is idempotent and re-validates every id, so a
//     reload mid-flow can never double-write votes or double-award.
//   • getWebBonusBallot returns `claimed:true` once the gate is stamped, so a
//     reload after claiming short-circuits to nothing (the overlay closes).
//   • sessionStorage (`ps_bonus_step`) remembers WHERE in the flow we were so a
//     reload after auth lands back on the ballot rather than re-showing the gate.
//   • The fetched cohort is cached in sessionStorage (`ps_bonus_cohort`) keyed by
//     uid so a reload mid-ballot re-renders the SAME question set instead of
//     re-fetching a possibly-different one (the set shifts as questions resolve).

const STEP_KEY = 'ps_bonus_step'; // 'gate' | 'ballot' | 'done'
const COHORT_KEY = 'ps_bonus_cohort'; // { uid, questions:[...] }

function loadStep() {
  try {
    return sessionStorage.getItem(STEP_KEY);
  } catch {
    return null;
  }
}
function saveStep(step) {
  try {
    if (step) sessionStorage.setItem(STEP_KEY, step);
    else sessionStorage.removeItem(STEP_KEY);
  } catch {
    /* storage unavailable — server gate still protects us */
  }
}
function loadCohort(uid) {
  try {
    const raw = sessionStorage.getItem(COHORT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.uid === uid && Array.isArray(parsed.questions)) return parsed.questions;
  } catch {
    /* ignore */
  }
  return null;
}
function saveCohort(uid, questions) {
  try {
    sessionStorage.setItem(COHORT_KEY, JSON.stringify({ uid, questions }));
  } catch {
    /* ignore */
  }
}
function clearPersist() {
  try {
    sessionStorage.removeItem(STEP_KEY);
    sessionStorage.removeItem(COHORT_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * @param {object}   props
 * @param {() => void} props.onClose  Called when the flow ends (claimed, skipped,
 *                                    or no cohort). The parent stamps a local
 *                                    "shown" flag so the overlay doesn't re-open.
 */
export default function BonusBallot({ onClose }) {
  const { uid, isAnonymous, openSaveAccount, saveAccountOpen } = useSession();

  // Resume the persisted step on mount. A brand-new entry starts at 'gate'
  // (or jumps straight to 'ballot' if the user is already secured — e.g. a
  // returning saved player who just played their first set on web).
  const [step, setStep] = useState(() => loadStep() || (isAnonymous ? 'gate' : 'ballot'));
  const [questions, setQuestions] = useState(() => (uid ? loadCohort(uid) : null));
  const [index, setIndex] = useState(0);
  const [picks, setPicks] = useState({}); // questionId -> boolean (skips omitted)
  const [phase, setPhase] = useState('idle'); // idle | loading | ready | empty | submitting | done | error
  const [written, setWritten] = useState(0);
  const fetchedRef = useRef(false);

  // Keep the persisted step in lockstep with state.
  useEffect(() => {
    saveStep(step);
  }, [step]);

  // The auth gate resolves when the session flips non-anonymous. The
  // SaveAccountModal links in place (same uid), so we just watch the flag.
  useEffect(() => {
    if (step === 'gate' && !isAnonymous) {
      logWebEvent('web_bonus_gate_passed', {});
      setStep('ballot');
    }
  }, [step, isAnonymous]);

  // Fetch the cohort once we're in the ballot step and secured. Reuse a cached
  // cohort from a prior (pre-reload) fetch so the question SET is stable across
  // a reload; only hit the server when we have none cached.
  useEffect(() => {
    if (step !== 'ballot' || isAnonymous || !uid) return;
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const cached = loadCohort(uid);
    if (cached && cached.length > 0) {
      setQuestions(cached);
      setPhase('ready');
      return;
    }

    setPhase('loading');
    (async () => {
      try {
        const res = await fn.getWebBonusBallot();
        const data = res?.data || {};
        if (data.claimed) {
          // Gate already stamped server-side (e.g. claimed on another device /
          // an earlier reload). Nothing to show — close cleanly.
          clearPersist();
          onClose?.();
          return;
        }
        const qs = Array.isArray(data.questions) ? data.questions : [];
        if (qs.length === 0) {
          // No eligible questions right now — don't strand the user on an empty
          // ballot. Stamp the gate by claiming an empty set so it won't re-prompt,
          // then close. (Best-effort; the server stamps the gate regardless.)
          try {
            await fn.claimWebBonusBallot({ answers: [] });
          } catch {
            /* the gate is stamped server-side on any successful claim; ignore */
          }
          setPhase('empty');
          clearPersist();
          logWebEvent('web_bonus_ballot_empty', {});
          return;
        }
        setQuestions(qs);
        saveCohort(uid, qs);
        setPhase('ready');
        logWebEvent('web_bonus_ballot_shown', { cohort_size: qs.length });
      } catch (e) {
        console.error('[BonusBallot] fetch failed:', e);
        setPhase('error');
        // Allow a retry on the next mount/effect run.
        fetchedRef.current = false;
      }
    })();
  }, [step, isAnonymous, uid, onClose]);

  const answeredCount = Object.keys(picks).length;
  const total = questions?.length ?? 0;

  // Record a pick (or skip when answer === null) and advance.
  const record = useCallback(
    (qid, answer) => {
      setPicks((prev) => {
        const next = { ...prev };
        if (answer === null) delete next[qid];
        else next[qid] = answer;
        return next;
      });
      setIndex((i) => Math.min(i + 1, total - 1));
    },
    [total]
  );

  const submit = useCallback(async () => {
    if (!questions || phase === 'submitting') return;
    setPhase('submitting');
    // Build the answers payload: a pick for each answered question, a skip for
    // the rest. The server re-validates every id against its own picker.
    const answers = questions.map((q) =>
      typeof picks[q.id] === 'boolean'
        ? { questionId: q.id, answer: picks[q.id] }
        : { questionId: q.id, isSkipped: true }
    );
    try {
      const res = await fn.claimWebBonusBallot({ answers });
      const data = res?.data || {};
      setWritten(typeof data.written === 'number' ? data.written : answeredCount);
      logWebEvent('web_bonus_ballot_claimed', {
        answered: answeredCount,
        written: data.written ?? answeredCount,
        already: data.reason === 'already-claimed',
      });
    } catch (e) {
      // The server gate is idempotent; even on a transient error the safest UX
      // is to show the done state and let the gate (set on any prior success)
      // prevent a re-prompt. We log it but don't strand the user.
      console.error('[BonusBallot] claim failed:', e);
      logWebEvent('web_bonus_ballot_claim_failed', {});
      setWritten(answeredCount);
    } finally {
      setPhase('done');
      setStep('done');
      clearPersist();
    }
  }, [questions, phase, picks, answeredCount]);

  const finish = useCallback(() => {
    clearPersist();
    onClose?.();
  }, [onClose]);

  // ── Render ────────────────────────────────────────────────────────────────

  // Gate step — the auth wall. We don't render the SaveAccount modal here (it's
  // mounted once at AppShell); we just present the pitch + trigger openSaveAccount.
  if (step === 'gate') {
    // While the SaveAccount modal is open, DON'T render this overlay. It sits
    // above the modal (z 60 > 50), so leaving it up hides the sign-up sheet and
    // makes "Sign up to continue" look dead — the reported bug. Dropping out lets
    // the modal show; on success isAnonymous flips false → the effect above
    // advances to 'ballot', and on cancel saveAccountOpen flips false → the gate
    // re-renders.
    if (saveAccountOpen) return null;
    // A user who is ALREADY signed up shouldn't be asked to sign up — give them a
    // plain "Continue" straight into the bonus round. (Anonymous players get the
    // account pitch + sign-up.)
    const securedAlready = !isAnonymous;
    return (
      <Overlay>
        <div style={center}>
          <div style={badge} aria-hidden="true">🔓</div>
          <h2 style={title}>Unlock 12 bonus questions</h2>
          <p style={sub}>
            {securedAlready
              ? 'Answer a bonus round on top of today’s six. Results roll in over the next few days as each one settles.'
              : 'Create your free account to answer a bonus round. Results roll in over the next few days as each one settles — and your picks, streak, and coins sync to every device.'}
          </p>
          <button
            style={primary}
            onClick={() => {
              if (securedAlready) {
                // Already secured — no sign-up needed, go straight to the ballot.
                logWebEvent('web_bonus_gate_continue_tapped', {});
                setStep('ballot');
                return;
              }
              logWebEvent('web_bonus_gate_save_tapped', {});
              openSaveAccount();
            }}
          >
            {securedAlready ? 'Continue' : 'Sign up to continue'}
          </button>
          <button style={secondary} onClick={finish}>
            Maybe later
          </button>
        </div>
      </Overlay>
    );
  }

  // Ballot step (loading / error / empty / ready).
  if (step === 'ballot') {
    if (phase === 'loading' || phase === 'idle') {
      return (
        <Overlay>
          <div style={center}>
            <p className="ps-muted">Loading your bonus questions…</p>
          </div>
        </Overlay>
      );
    }
    if (phase === 'error') {
      return (
        <Overlay>
          <div style={center}>
            <p className="ps-muted">Couldn’t load the bonus questions.</p>
            <button
              style={primary}
              onClick={() => {
                fetchedRef.current = false;
                setPhase('idle');
              }}
            >
              Try again
            </button>
            <button style={secondary} onClick={finish}>
              Skip for now
            </button>
          </div>
        </Overlay>
      );
    }
    if (phase === 'empty') {
      return (
        <Overlay>
          <div style={center}>
            <div style={badge} aria-hidden="true">✓</div>
            <h2 style={title}>You’re all caught up</h2>
            <p style={sub}>
              No bonus questions right now — you’ve answered everything open. A new
              six drops every day.
            </p>
            <button style={primary} onClick={finish}>
              Done
            </button>
          </div>
        </Overlay>
      );
    }
    // ready — render the current card.
    const q = questions[index];
    const lastCard = index >= total - 1;
    return (
      <Overlay>
        <div style={ballotWrap}>
          <header style={ballotHeader}>
            <h2 style={{ ...title, margin: '0 0 4px', fontSize: 22 }}>Bonus round</h2>
            <p className="ps-muted" style={{ margin: 0, fontSize: 14 }}>
              {answeredCount} of {total} answered
            </p>
            <div style={progressTrack} aria-hidden="true">
              <div
                style={{
                  ...progressFill,
                  width: `${total ? (Math.min(index, total) / total) * 100 : 0}%`,
                }}
              />
            </div>
          </header>

          {q && <BonusCard key={q.id} question={q} pick={picks[q.id]} onAnswer={(a) => record(q.id, a)} />}

          <div style={ballotFooter}>
            {!lastCard && (
              <button style={skipBtn} onClick={() => record(q.id, null)}>
                Skip this one
              </button>
            )}
            {lastCard && (
              // Single CTA on the final card. Always shown (even if the last
              // question is unanswered — it's filled as a skip server-side), so
              // there's exactly one "Submit", not the old Finish + Submit pair.
              <button
                style={primary}
                onClick={submit}
                disabled={phase === 'submitting'}
              >
                {phase === 'submitting' ? 'Submitting…' : 'Submit'}
              </button>
            )}
          </div>
        </div>
      </Overlay>
    );
  }

  // Submitting / done.
  if (phase === 'submitting') {
    return (
      <Overlay>
        <div style={center}>
          <p className="ps-muted">Locking in your bonus answers…</p>
        </div>
      </Overlay>
    );
  }
  // done
  return (
    <Overlay>
      <div style={center}>
        <div style={badge} aria-hidden="true">✓</div>
        <h2 style={title}>Bonus round locked in</h2>
        <p style={sub}>
          {written > 0
            ? `You answered ${written} bonus ${written === 1 ? 'question' : 'questions'}. ` +
              'Results roll in over the next few days as each one settles.'
            : 'All set — results roll in over the next few days as each question settles.'}
        </p>
        <button style={primary} onClick={finish}>
          Done
        </button>
      </div>
    </Overlay>
  );
}

// A single bonus card. Lighter than QuestionCard (no share, no resolved row —
// the picker guarantees these are open) but mirrors its YES/NO affordance.
function BonusCard({ question: q, pick, onAnswer }) {
  const est = q.estimatedResolutionDate ? new Date(q.estimatedResolutionDate) : null;
  return (
    <div className="ps-card" style={{ marginBottom: 14 }}>
      {q.imageURL && (
        <img
          src={q.imageURL}
          alt={q.category ? `${q.category} question illustration` : ''}
          loading="lazy"
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
          style={{
            width: '100%',
            aspectRatio: '4 / 3',
            objectFit: 'contain',
            borderRadius: 'var(--radius-sm)',
            marginBottom: 12,
            display: 'block',
            background: 'var(--surface)',
          }}
        />
      )}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 10,
        }}
      >
        <span className="ps-chip">{q.category}</span>
        {est && (
          <span className="ps-chip" style={{ color: 'var(--gold)' }}>
            {resolvesLabel(est)}
          </span>
        )}
      </div>
      <p style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.32, margin: '0 0 14px' }}>
        {q.text}
      </p>
      <div style={{ display: 'flex', gap: 10 }}>
        <BonusChoice label="YES" selected={pick === true} onClick={() => onAnswer(true)} />
        <BonusChoice label="NO" selected={pick === false} onClick={() => onAnswer(false)} />
      </div>
    </div>
  );
}

function BonusChoice({ label, selected, onClick }) {
  return (
    <button
      onClick={onClick}
      aria-pressed={selected}
      style={{
        flex: 1,
        padding: '14px 0',
        borderRadius: 'var(--radius-sm)',
        border: `1.5px solid ${selected ? 'var(--gold)' : 'var(--border)'}`,
        background: selected ? 'var(--gold)' : 'var(--surface)',
        color: selected ? '#1a1a1a' : 'var(--primary-text)',
        fontWeight: 800,
        fontSize: 16,
        letterSpacing: '0.04em',
        cursor: 'pointer',
        transition: 'background 120ms, border-color 120ms',
      }}
    >
      {label}
    </button>
  );
}

// Full-screen overlay shell — sits above the play screen + bottom nav.
function Overlay({ children }) {
  return (
    <div style={overlay} role="dialog" aria-modal="true" aria-label="Bonus round">
      <div style={overlayInner}>{children}</div>
    </div>
  );
}

const overlay = {
  position: 'fixed',
  inset: 0,
  zIndex: 60, // above the bottom nav + SaveAccount backdrop (50)
  background: 'var(--bg, #0d0d0d)',
  overflowY: 'auto',
  display: 'flex',
  justifyContent: 'center',
};
const overlayInner = {
  width: '100%',
  maxWidth: 'var(--maxw)',
  padding: '24px 16px calc(28px + env(safe-area-inset-bottom))',
  boxSizing: 'border-box',
  minHeight: '100%',
  display: 'flex',
  flexDirection: 'column',
};
const center = {
  margin: 'auto',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 10,
  maxWidth: 420,
};
const badge = {
  fontSize: 44,
  lineHeight: 1,
  marginBottom: 4,
};
const title = {
  fontWeight: 900,
  fontSize: 24,
  margin: '0 0 4px',
  color: 'var(--primary-text)',
};
const sub = {
  margin: '0 0 14px',
  fontSize: 15,
  lineHeight: 1.5,
  color: 'var(--secondary-text)',
};
const primary = {
  width: '100%',
  maxWidth: 320,
  padding: '14px 0',
  borderRadius: 'var(--radius-sm)',
  border: 'none',
  background: 'var(--gold)',
  color: '#1a1a1a',
  fontWeight: 800,
  fontSize: 15,
  cursor: 'pointer',
};
const secondary = {
  width: '100%',
  maxWidth: 320,
  marginTop: 2,
  padding: '10px 0',
  border: 'none',
  background: 'transparent',
  color: 'var(--secondary-text)',
  fontWeight: 700,
  fontSize: 14,
  cursor: 'pointer',
};
const ballotWrap = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
};
const ballotHeader = {
  textAlign: 'center',
  marginBottom: 16,
};
const progressTrack = {
  marginTop: 10,
  height: 4,
  borderRadius: 2,
  background: 'var(--surface)',
  overflow: 'hidden',
};
const progressFill = {
  height: '100%',
  background: 'var(--gold)',
  transition: 'width 200ms ease',
};
const ballotFooter = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  marginTop: 'auto',
  paddingTop: 12,
};
const skipBtn = {
  width: '100%',
  padding: '12px 0',
  borderRadius: 'var(--radius-sm)',
  border: '1.5px solid var(--border)',
  background: 'transparent',
  color: 'var(--secondary-text)',
  fontWeight: 700,
  fontSize: 14,
  cursor: 'pointer',
};
